import apiClient from "./client";
import { fetchAllPages } from "./pagination";
import { buildJsonApiQuery, withoutIncludes } from '@/utils/jsonapi';

/**
 * @typedef {import('@/types/models/metric').Metric} Metric
 * @typedef {import('@/types/models/metric').MetricType} MetricType
 * @typedef {import('@/types/api/responses').PaginatedResponse} PaginatedResponse
 */

/**
 * Metrics API module
 * Returns raw JSON:API responses - normalization happens in Redux thunks
 */
const metricsAPI = {
  /**
   * Get one page of metrics with optional filtering
   * @param {Object} [queryOptions] - JSON:API query options
   * @param {Object} [queryOptions.filters] - Filter criteria (e.g., { category: 'uuid', is_active: true })
   * @param {Array<string>|string} [queryOptions.sort] - Sort fields (e.g., ['name', '-created_at'])
   * @param {Array<string>|string} [queryOptions.include] - Relations to include (e.g., ['category', 'created_by'])
   * @param {number} [queryOptions.page] - Page number
   * @param {number} [queryOptions.pageSize] - Items per page
   * @returns {Promise<Object>} JSON:API response with data, meta, links
   */
  getAll: async (queryOptions = {}, requestOptions = {}) => {
    const params = buildJsonApiQuery(queryOptions);
    const response = await apiClient.get("/metrics/", { ...requestOptions, params });
    return response.data;
  },

  /**
   * Load the complete filtered catalog for screens that search locally.
   * The server caps pages at 100; increasing pageSize alone cannot fetch all
   * metrics. Keep getAll's single-page contract for paginated pickers.
   *
   * @param {Object} [queryOptions] - Filters, sorting, includes and page size
   * @param {Object} [requestOptions] - Axios signal and pinned tenant headers
   * @returns {Promise<{data: Array, included: Array, count: number}>}
   */
  getAllPages: (queryOptions = {}, requestOptions = {}) =>
    fetchAllPages(metricsAPI.getAll, queryOptions, requestOptions),

  /**
   * Get a specific metric by ID
   * @param {string} metricId - Metric UUID
   * @param {Object} [queryOptions] - JSON:API query options
   * @returns {Promise<Object>} JSON:API response with metric data
   */
  getById: async (metricId, queryOptions = {}) => {
    // Default to including category and partition configurations with partition types
    const defaultIncludes = 'category,partition_configurations.partition_type';
    const params = buildJsonApiQuery({
      ...queryOptions,
      include: queryOptions.include || defaultIncludes
    });
    const response = await apiClient.get(`/metrics/${metricId}/`, { params });
    return response.data;
  },

  /**
   * Create a new metric
   * @param {Object} data - Metric data
   * @param {string} data.name - Metric name
   * @param {string} data.description - Metric description
   * @param {MetricType} data.metric_type - Type of metric
   * @param {string} data.category_id - Category UUID
   * @param {Object} [data.partition_configuration] - Partition config
   * @returns {Promise<Object>} JSON:API response with created metric
   */
  create: async (data) => {
    const response = await apiClient.post("/metrics/", data);
    return response.data;
  },

  /**
   * Update a metric
   * @param {string} metricId - Metric UUID
   * @param {Object} data - Updated metric data
   * @param {Object} [queryOptions] - JSON:API query options
   * @returns {Promise<Object>} JSON:API response with updated metric
   */
  update: async (metricId, data, queryOptions = {}) => {
    // Extract partition_configurations to put in relationships
    const { partition_configurations, ...attributes } = data;

    // Build JSON:API payload
    const payload = {
      data: {
        type: 'metric',
        id: metricId,
        attributes,
        ...(partition_configurations && {
          relationships: {
            partition_configurations: {
              data: partition_configurations
            }
          }
        })
      }
    };

    // Default to including partition configurations with partition types
    const defaultIncludes = 'partition_configurations.partition_type';
    const params = buildJsonApiQuery({
      ...queryOptions,
      include: queryOptions.include || defaultIncludes
    });
    const response = await apiClient.patch(`/metrics/${metricId}/`, payload, { params });
    return response.data;
  },

  /**
   * Delete a metric
   * @param {string} metricId - Metric UUID
   * @returns {Promise<void>}
   */
  delete: async (metricId) => {
    await apiClient.delete(`/metrics/${metricId}/`);
  },

  /**
   * Activate a metric.
   *
   * Activation has its own endpoints and its own role (Administrator).
   * Patching is_active is refused by the API, so this is the only route.
   *
   * @param {string} metricId - Metric UUID
   * @returns {Promise<Object>} JSON:API response with the updated metric
   */
  activate: async (metricId) => {
    const response = await apiClient.post(`/metrics/${metricId}/activate/`);
    return response.data;
  },

  /**
   * Deactivate a metric. See activate() for why this is not a PATCH.
   * @param {string} metricId - Metric UUID
   * @returns {Promise<Object>} JSON:API response with the updated metric
   */
  deactivate: async (metricId) => {
    const response = await apiClient.post(`/metrics/${metricId}/deactivate/`);
    return response.data;
  },

  /**
   * Create a metric from one of the seeded measurement templates (design
   * doc §3; E1/E2E-02 add `source: 'derived'`), without requiring a
   * Rock/PCO connection. Rock/PCO-backed template creation still goes
   * through the measurements board's `CreateFromTemplateDialog` Rock
   * wizard, which posts to `startMeasurementsSetup` instead.
   *
   * `source: 'manual'` (the default) builds a plain metric from the
   * template's name/description/metric_type; when `seedSampleData` is true
   * the backend also creates 12 weeks of plausible values flagged
   * `is_sample: true`, reported back as `response.meta.sample_entries_created`.
   *
   * `source: 'derived'` (the health-ratio KPI pack - a template whose
   * `providers` list includes `'graphit'`) instead builds a calculated
   * ratio/rolling-average metric from the template's own input refs; it
   * has no observations of its own, so `seed_sample_data` is never sent
   * for it regardless of what the caller passes (E2E-02: sending
   * `source: 'manual'` for a KPI template used to silently create a plain
   * summed metric filled with fabricated sample numbers).
   *
   * @param {Object} params
   * @param {string} params.templateKey - `MetricTemplate.key`
   * @param {boolean} [params.seedSampleData=false]
   * @param {'manual'|'derived'} [params.source='manual']
   * @returns {Promise<Object>} JSON:API response `{data, meta}` - `data` is
   *   the created metric, `meta.sample_entries_created` the seeded count
   */
  createFromTemplate: async ({ templateKey, seedSampleData = false, source = 'manual' }) => {
    const response = await apiClient.post('/metrics/from-template/', {
      template_key: templateKey,
      source,
      seed_sample_data: source === 'derived' ? false : seedSampleData,
    });
    return response.data;
  },

  /**
   * Delete every `is_sample` entry a template created for this metric,
   * leaving the metric itself (and any real entries) in place.
   *
   * @param {string} metricId - Metric UUID
   * @returns {Promise<Object>} JSON:API response with the updated metric
   */
  clearSampleData: async (metricId) => {
    const response = await apiClient.post(`/metrics/${metricId}/clear-sample-data/`);
    return response.data;
  },

  /**
   * Get what still uses a metric, before deactivating or deleting it.
   *
   * Any tenant member may read this. The response carries every reference -
   * dashboards, reports, forms and integration syncs - with the route each one
   * opens at, plus `can_deactivate`, which is the only reliable answer to
   * whether the API will allow the change.
   *
   * Includes are switched off: this is a computed resource with no
   * relationships, so the default `?include=all` would only cost a round trip.
   *
   * @param {string} metricId - Metric UUID
   * @returns {Promise<Object>} JSON:API response with a `metric_usage` resource
   */
  getUsage: async (metricId) => {
    const response = await apiClient.get(`/metrics/${metricId}/usage/`, {
      params: withoutIncludes(),
    });
    return response.data;
  },

  /**
   * The values a metric's trend chart draws, one row per period and campus.
   *
   * Activity metrics (their values live in imported activity) and calculated
   * metrics have no entries for `/metric-entries/` to list; this runs the
   * chart's own calculation instead, narrowed by the caller's campuses.
   *
   * @param {string} metricId - Metric UUID
   * @param {Object} params
   * @param {string} params.range - relative range, e.g. `last_90_days`
   * @param {string} params.interval - `day`, `week` or `month`
   * @param {Object} [requestOptions] - Axios options such as `signal`
   * @returns {Promise<Object>} JSON:API response with a `metric_observations` resource
   */
  getObservations: async (metricId, { range, interval }, requestOptions = {}) => {
    const response = await apiClient.get(`/metrics/${metricId}/observations/`, {
      ...requestOptions,
      params: { ...withoutIncludes(), range, interval },
    });
    return response.data;
  },

  /**
   * Get metric values/data points
   * @param {string} metricId - Metric UUID
   * @param {Object} [params] - Query parameters
   * @param {string} [params.start_date] - Start date filter
   * @param {string} [params.end_date] - End date filter
   * @param {string} [params.partition_value] - Partition filter
   * @returns {Promise<Object>} JSON:API response with metric values
   */
  getValues: async (metricId, params = {}) => {
    const response = await apiClient.get(`/metrics/${metricId}/values`, {
      params,
    });
    return response.data;
  },

  /**
   * Submit metric value(s)
   * @param {string} metricId - Metric UUID
   * @param {Object} data - Value data
   * @param {number|Array<number>} data.value - Metric value(s)
   * @param {string} [data.partition_value] - Partition value
   * @param {string} [data.timestamp] - Timestamp (defaults to now)
   * @returns {Promise<Object>} JSON:API response with submitted value
   */
  submitValue: async (metricId, data) => {
    const response = await apiClient.post(`/metrics/${metricId}/values`, data);
    return response.data;
  },

  /**
   * Bulk submit metric values
   * @param {Array<Object>} data - Array of metric submissions
   * @returns {Promise<Object>} JSON:API response with bulk submission results
   */
  bulkSubmit: async (data) => {
    const response = await apiClient.post("/metrics/bulk/", data);
    return response.data;
  },

  /**
   * Add a partition type to a metric
   * @param {string} metricId - Metric UUID
   * @param {Object} data - Partition configuration
   * @param {string} data.partition_type_id - Partition type UUID
   * @param {boolean} data.is_required - Whether partition is required
   * @returns {Promise<Object>} JSON:API response
   */
  addPartitionType: async (metricId, data) => {
    const response = await apiClient.post(
      `/metrics/${metricId}/partition-types`,
      data
    );
    return response.data;
  },

  /**
   * Remove a partition type from a metric
   * @param {string} metricId - Metric UUID
   * @param {string} partitionTypeId - Partition type UUID
   * @returns {Promise<void>}
   */
  removePartitionType: async (metricId, partitionTypeId) => {
    await apiClient.delete(
      `/metrics/${metricId}/partition-types/${partitionTypeId}`
    );
  },

  /**
   * Check partition type usage in metric entries
   * @param {string} metricId - Metric UUID
   * @param {string} partitionTypeId - Partition type UUID
   * @returns {Promise<Object>} JSON:API response with usage data
   */
  checkPartitionUsage: async (metricId, partitionTypeId) => {
    const response = await apiClient.get(
      `/metrics/${metricId}/partition-types/${partitionTypeId}/usage`
    );
    return response.data;
  },
};

export default metricsAPI;
